import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Search, Loader2, Users, Pencil, UserCheck, UserX, Download,
  Briefcase, DollarSign, KeyRound, Eye, EyeOff, LayoutGrid, List,
  GraduationCap, ShieldCheck, Car, Sparkles,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AddStaffDialog from "@/components/dialogs/AddStaffDialog";
import EditStaffDialog from "@/components/dialogs/EditStaffDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import { getSignedFileUrl } from "@/lib/storage-utils";
import { exportToCSV } from "@/lib/export-utils";

const roleLabels: Record<string, string> = {
  teacher: "مدرس/ة",
  supervisor: "مشرف/ة",
  admin: "إداري/ة",
  driver: "سائق",
  cleaner: "عامل نظافة",
};

const salaryTypeLabels: Record<string, string> = {
  monthly: "شهري",
  per_session: "بالحصة",
  percentage: "نسبة",
};

const roleIcons: Record<string, any> = {
  teacher: GraduationCap,
  supervisor: ShieldCheck,
  admin: Briefcase,
  driver: Car,
  cleaner: Sparkles,
};

const Staff = () => {
  const { profile } = useAuth();
  const [staffList, setStaffList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterAccount, setFilterAccount] = useState("all");
  const [editingStaff, setEditingStaff] = useState<any | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  // Reset password state
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwStaff, setResetPwStaff] = useState<any>(null);
  const [resetPwValue, setResetPwValue] = useState("");
  const [resetPwLoading, setResetPwLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const fetchStaff = async () => {
    if (!profile?.nursery_id) return;
    setLoading(true);
    const { data } = await supabase
      .from("staff")
      .select("*")
      .eq("nursery_id", profile.nursery_id)
      .order("created_at", { ascending: false });
    setStaffList(data || []);
    setLoading(false);

    if (data) {
      const urls: Record<string, string> = {};
      for (const s of data) {
        if (s.photo_url) {
          const url = await getSignedFileUrl("staff-photos", s.photo_url);
          if (url) urls[s.id] = url;
        }
      }
      setPhotoUrls(urls);
    }
  };

  useEffect(() => {
    if (profile?.nursery_id) fetchStaff();
  }, [profile?.nursery_id]);

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("staff").delete().eq("id", id);
    if (error) {
      toast.error("خطأ في حذف الموظف: " + error.message);
    } else {
      toast.success("تم حذف الموظف بنجاح");
      fetchStaff();
    }
  };

  const getNetSalary = (s: any) => {
    if (s.salary_type === "per_session") return null;
    if (s.salary_type === "percentage") return null;
    const salary = Number(s.salary) || 0;
    const allowances = Number(s.allowances) || 0;
    const bonuses = Number(s.bonuses) || 0;
    const deductions = Number(s.deductions) || 0;
    return salary + allowances + bonuses - deductions;
  };

  const getSalaryDisplay = (s: any) => {
    const type = s.salary_type || "monthly";
    if (type === "per_session") return `${Number(s.session_price || 0).toLocaleString()} / حصة`;
    if (type === "percentage") return `${Number(s.commission_percentage || 0)}%`;
    const net = getNetSalary(s);
    return net && net > 0 ? `${net.toLocaleString()} ${currency}` : "—";
  };

  // Stats
  const stats = useMemo(() => {
    const total = staffList.length;
    const withAccount = staffList.filter((s) => s.user_id).length;
    const totalSalary = staffList.reduce((sum, s) => sum + (getNetSalary(s) || 0), 0);
    const byRole: Record<string, number> = {};
    staffList.forEach((s) => {
      byRole[s.role] = (byRole[s.role] || 0) + 1;
    });
    return { total, withAccount, withoutAccount: total - withAccount, totalSalary, byRole };
  }, [staffList]);

  // Filtered
  const filtered = useMemo(() => {
    return staffList.filter((s) => {
      if (search && !s.full_name.toLowerCase().includes(search.toLowerCase()) && !(s.email || "").toLowerCase().includes(search.toLowerCase())) return false;
      if (filterRole !== "all" && s.role !== filterRole) return false;
      if (filterAccount === "active" && !s.user_id) return false;
      if (filterAccount === "none" && s.user_id) return false;
      return true;
    });
  }, [staffList, search, filterRole, filterAccount]);

  const handleExport = () => {
    if (!filtered.length) return;
    const rows = filtered.map((s) => ({
      "الاسم": s.full_name,
      "الوظيفة": roleLabels[s.role] || s.role,
      "نوع الراتب": salaryTypeLabels[s.salary_type] || "شهري",
      "الهاتف": s.phone || "",
      "البريد": s.email || "",
      "الراتب الأساسي": s.salary || 0,
      "سعر الحصة": s.session_price || 0,
      "النسبة %": s.commission_percentage || 0,
      "البدلات": s.allowances || 0,
      "العلاوات": s.bonuses || 0,
      "الخصومات": s.deductions || 0,
      "صافي الراتب": getNetSalary(s) ?? "متغير",
      "حساب مفعل": s.user_id ? "نعم" : "لا",
      "تاريخ الإضافة": s.created_at ? format(new Date(s.created_at), "dd/MM/yyyy") : "",
    }));
    exportToCSV(rows, "staff-report");
    toast.success("تم تصدير التقرير");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetPwValue.length < 6) {
      toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    setResetPwLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-parent-password", {
        body: { parent_user_id: resetPwStaff.user_id, new_password: resetPwValue },
      });
      if (error || data?.error) {
        toast.error("خطأ: " + (data?.error || error?.message));
      } else {
        toast.success("تم تغيير كلمة المرور بنجاح");
        setResetPwOpen(false);
        setResetPwValue("");
      }
    } catch {
      toast.error("حدث خطأ غير متوقع");
    } finally {
      setResetPwLoading(false);
    }
  };

  const currency = (staffList[0] as any)?.currency || "EGP";

  const StaffRow = ({ s }: { s: any }) => {
    const RoleIcon = roleIcons[s.role] || Briefcase;
    return (
      <TableRow key={s.id} className="hover:bg-muted/30 transition-colors">
        <TableCell>
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9">
              <AvatarImage src={photoUrls[s.id] || ""} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                {s.full_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <span className="font-medium text-card-foreground">{s.full_name}</span>
              {s.email && <p className="text-xs text-muted-foreground" dir="ltr">{s.email}</p>}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-[10px] gap-1">
            <RoleIcon className="w-3 h-3" />
            {roleLabels[s.role] || s.role}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground text-sm" dir="ltr">{s.phone || "—"}</TableCell>
        <TableCell className="text-sm">
          <div>
            <span className="font-medium">{getSalaryDisplay(s)}</span>
            {s.salary_type && s.salary_type !== "monthly" && (
              <Badge variant="outline" className="text-[9px] mr-1">{salaryTypeLabels[s.salary_type]}</Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          {s.user_id ? (
            <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 text-[10px]">
              <UserCheck className="w-3 h-3 ml-1" /> مفعّل
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground text-[10px]">
              <UserX className="w-3 h-3 ml-1" /> بدون حساب
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {s.created_at ? format(new Date(s.created_at), "dd/MM/yyyy") : "—"}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingStaff(s)}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>تعديل</TooltipContent>
            </Tooltip>
            {s.user_id && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setResetPwStaff(s); setResetPwOpen(true); }}>
                    <KeyRound className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>إعادة تعيين كلمة المرور</TooltipContent>
              </Tooltip>
            )}
            <DeleteConfirmDialog
              title="حذف الموظف"
              description={`هل أنت متأكد من حذف "${s.full_name}"؟ لا يمكن التراجع عن هذا الإجراء.`}
              onConfirm={() => handleDelete(s.id)}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const StaffCard = ({ s }: { s: any }) => {
    const RoleIcon = roleIcons[s.role] || Briefcase;
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={photoUrls[s.id] || ""} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {s.full_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-card-foreground truncate">{s.full_name}</h3>
              <Badge variant="outline" className="text-[10px] gap-1 mt-1">
                <RoleIcon className="w-3 h-3" />
                {roleLabels[s.role] || s.role}
              </Badge>
            </div>
            {s.user_id ? (
              <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 text-[10px]">
                <UserCheck className="w-3 h-3" />
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground text-[10px]">
                <UserX className="w-3 h-3" />
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>📱 {s.phone || "—"}</div>
            <div>💰 {getSalaryDisplay(s)}</div>
            {s.email && <div className="col-span-2 truncate" dir="ltr">✉️ {s.email}</div>}
          </div>
          <div className="flex items-center gap-1 pt-1 border-t">
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setEditingStaff(s)}>
              <Pencil className="w-3 h-3" /> تعديل
            </Button>
            {s.user_id && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => { setResetPwStaff(s); setResetPwOpen(true); }}>
                <KeyRound className="w-3 h-3" /> كلمة المرور
              </Button>
            )}
            <div className="mr-auto">
              <DeleteConfirmDialog
                title="حذف الموظف"
                description={`هل أنت متأكد من حذف "${s.full_name}"؟`}
                onConfirm={() => handleDelete(s.id)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            إدارة الموظفين
          </h1>
          <p className="text-muted-foreground text-sm">إضافة وتعديل بيانات الموظفين وإنشاء حساباتهم</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered.length}>
            <Download className="w-4 h-4 ml-1" />
            تصدير
          </Button>
          <AddStaffDialog onSuccess={fetchStaff} />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10"><Users className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الموظفين</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/10"><UserCheck className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">حسابات مفعّلة</p>
              <p className="text-2xl font-bold">{stats.withAccount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/10"><UserX className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">بدون حساب</p>
              <p className="text-2xl font-bold">{stats.withoutAccount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-500/10"><DollarSign className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الرواتب</p>
              <p className="text-lg font-bold">{stats.totalSalary.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">{currency}</span></p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث بالاسم أو البريد..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-36"><SelectValue placeholder="الوظيفة" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الوظائف</SelectItem>
                {Object.entries(roleLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterAccount} onValueChange={setFilterAccount}>
              <SelectTrigger className="w-36"><SelectValue placeholder="حالة الحساب" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="active">حساب مفعّل</SelectItem>
                <SelectItem value="none">بدون حساب</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex border rounded-md">
              <Button variant={viewMode === "table" ? "secondary" : "ghost"} size="icon" className="h-9 w-9 rounded-l-none" onClick={() => setViewMode("table")}>
                <List className="w-4 h-4" />
              </Button>
              <Button variant={viewMode === "cards" ? "secondary" : "ghost"} size="icon" className="h-9 w-9 rounded-r-none" onClick={() => setViewMode("cards")}>
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">
              {search || filterRole !== "all" || filterAccount !== "all" ? "لا توجد نتائج للبحث" : "لم يتم إضافة موظفين بعد"}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === "table" ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              الموظفين ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الموظف</TableHead>
                    <TableHead>الوظيفة</TableHead>
                    <TableHead>الهاتف</TableHead>
                    <TableHead>صافي الراتب</TableHead>
                    <TableHead>الحساب</TableHead>
                    <TableHead>تاريخ الإضافة</TableHead>
                    <TableHead>إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => <StaffRow key={s.id} s={s} />)}
                </TableBody>
              </Table>
            </TooltipProvider>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => <StaffCard key={s.id} s={s} />)}
        </div>
      )}

      {/* Edit Dialog */}
      {editingStaff && (
        <EditStaffDialog
          staff={editingStaff}
          open={!!editingStaff}
          onOpenChange={(open) => !open && setEditingStaff(null)}
          onSuccess={fetchStaff}
        />
      )}

      {/* Reset Password Dialog */}
      <Dialog open={resetPwOpen} onOpenChange={(o) => { setResetPwOpen(o); if (!o) { setResetPwValue(""); setShowPw(false); } }}>
        <DialogContent dir="rtl" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <KeyRound className="w-4 h-4 text-primary" />
              إعادة تعيين كلمة المرور
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              تعيين كلمة مرور جديدة لحساب <span className="font-semibold text-foreground">{resetPwStaff?.full_name}</span>
            </p>
            <div>
              <Label>كلمة المرور الجديدة</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={resetPwValue}
                  onChange={(e) => setResetPwValue(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  dir="ltr"
                  className="pl-10"
                />
                <button
                  type="button"
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPw(!showPw)}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">6 أحرف على الأقل</p>
            </div>
            <Button type="submit" className="w-full" disabled={resetPwLoading}>
              {resetPwLoading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              تغيير كلمة المرور
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Staff;
